import React, {
  Dispatch, Ref, SetStateAction, useEffect, useRef, useState,
} from 'react';
import {
  View, StyleSheet, Text, TextInput, ScrollView, Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import DropdownIcon from '../../16pxIcons/DropdownIcon';
import FlagIcon16 from '../../16pxIcons/FlagIcon16';
import { priorityColor, priorityTitle } from './FloorItem';
import FloorPlanBox from './FloorPlanBox';
import PressableStyled from '../../PressableStyled';

export const maxFloorNameLength = 50;
export const maxFloorNumberLength = 5;

interface FloorDetailsFormProps {
  currentFloorName: string,
  currentFloorNumber: string,
  currentFloorExternalId: string,
  currentFloorHighPriority: boolean,
  currentFloorPlan: string,
  currentFloorPlanSvg: string,
  currentFloorPlanSize: number,
  currentFloorPlanFilename: string,
  floorNameError: string | null,
  floorNumberError: string | null,
  floorExternalIdError: string | null,
  priorityDropdownShown: boolean,
  // eslint-disable-next-line no-unused-vars
  editFloorName: (text: string) => void,
  // eslint-disable-next-line no-unused-vars
  editFloorNumber: (text: string) => void,
  // eslint-disable-next-line no-unused-vars
  editFloorExternalId: (externalId: string) => void,
  setCurrentFloorHighPriority: Dispatch<SetStateAction<boolean>>,
  setCurrentFloorPlan: Dispatch<SetStateAction<string>>,
  setCurrentFloorPlanSize: Dispatch<SetStateAction<number>>,
  setCurrentFloorPlanFilename: Dispatch<SetStateAction<string>>,
  setPriorityDropdownShown: Dispatch<SetStateAction<boolean>>,
  originalFloorExternalId: string,
}

const FloorDetailsForm = ({
  currentFloorName, currentFloorNumber, currentFloorHighPriority, currentFloorPlan,
  currentFloorPlanSize, currentFloorPlanFilename, priorityDropdownShown, currentFloorPlanSvg,
  floorNameError, floorNumberError, editFloorName, editFloorNumber, setCurrentFloorPlan,
  setCurrentFloorPlanFilename, setCurrentFloorPlanSize, setCurrentFloorHighPriority,
  setPriorityDropdownShown, currentFloorExternalId, floorExternalIdError, editFloorExternalId,
  originalFloorExternalId,
}: FloorDetailsFormProps) => {
  const scrollView: Ref<ScrollView> = useRef<ScrollView>(null);
  const [clientId, setClientId] = useState('hereworks');

  useEffect(() => {
    AsyncStorage.getItem('clientName').then((n) => {
      setClientId(n);
    });
  }, []);

  return <ScrollView
    style={styles.editFloorContainer}
    ref={scrollView}
  >
    <Text style={styles.description}>{'Fields marked with * are mandatory'}</Text>
    <View style={[Style.row, styles.textInputRow]}>
      <View style={styles.textField}>
        <Text style={[styles.description, styles.textInputTitle]}>{'Floor name *'}</Text>
        <TextInput
          style={[styles.textInput, floorNameError && styles.textInputError]}
          maxLength={maxFloorNameLength}
          value={currentFloorName ?? ''}
          onChangeText={editFloorName}
          placeholder='Enter floor name'
          placeholderTextColor={Colors.light.hereworksBlack400}
          testID='lm-floor-name-input-field'
        />
        {floorNameError && <Text style={styles.errorText}>{floorNameError}</Text>}
      </View>
      <View style={styles.textField}>
        <Text style={[styles.description, styles.textInputTitle]}>{'Floor number *'}</Text>
        <TextInput
          style={[styles.textInput, floorNumberError && styles.textInputError]}
          value={currentFloorNumber ?? ''}
          maxLength={maxFloorNumberLength}
          onChangeText={editFloorNumber}
          placeholder='Enter floor number'
          placeholderTextColor={Colors.light.hereworksBlack400}
          testID='lm-floor-number-input-field'
        />
        {floorNumberError && <Text style={styles.errorText}>{floorNumberError}</Text>}
      </View>
      <PressableStyled
        style={[
          Style.row, styles.priorityIndicator,
          { borderColor: priorityColor(currentFloorHighPriority) },
        ]}
        onPress={() => setPriorityDropdownShown(!priorityDropdownShown)}
        testID='floor-priority-picker'
      >
        <FlagIcon16 color={priorityColor(currentFloorHighPriority)} />
        <Text style={[styles.priorityText, { color: priorityColor(currentFloorHighPriority) }]}>
          {priorityTitle(currentFloorHighPriority)}
        </Text>
        <DropdownIcon
          color={priorityColor(currentFloorHighPriority)}
          style={[
            styles.dropdownIcon, priorityDropdownShown ? Style.inverted : Style.upright]}
        />
      </PressableStyled>
    </View>
    <View>
      {priorityDropdownShown && <View style={styles.priorityDropdown}>
        {[true, false].map((option: boolean, index: number) => <PressableStyled
          onPress={() => {
            setCurrentFloorHighPriority(option);
            setPriorityDropdownShown(false);
          }}
          key={`priority_option_${index}`}
        >
          <Text style={[
            styles.optionText,
            (option) === currentFloorHighPriority && styles.optionTextSelected,
          ]}>
            {priorityTitle(option)}
          </Text>
        </PressableStyled>)}
      </View>}
    </View>
    <FloorPlanBox
      onPress={() => setPriorityDropdownShown(false)}
      plan={currentFloorPlan}
      planSize={currentFloorPlanSize}
      planFilename={currentFloorPlanFilename}
      Svg={currentFloorPlanSvg}
      updatePlan={(plan: string) => setCurrentFloorPlan(plan)}
      updatePlanSize={(size: number) => setCurrentFloorPlanSize(size)}
      updatePlanFilename={(filename: string) => {
        setCurrentFloorPlanFilename(filename);
        const timeout = setTimeout(() => {
          scrollView?.current?.scrollToEnd();
          clearTimeout(timeout);
        }, 500);
      }}
    />
    <Text style={[styles.description, styles.textInputTitle, { marginTop: 16 }]}>
      {'External ID'}
    </Text>
    <TextInput
      style={[styles.textInput, floorExternalIdError && styles.textInputError]}
      value={currentFloorExternalId ?? ''}
      onChangeText={editFloorExternalId}
      placeholder='Enter external ID'
      placeholderTextColor={Colors.light.hereworksBlack400}
      testID='lm-floor-name-external-id-field'
      editable={clientId !== 'lazard' || !originalFloorExternalId}
    />
    {floorExternalIdError && <Text style={styles.errorText}>{floorExternalIdError}</Text>}
    {(clientId === 'lazard' && originalFloorExternalId) && <Text style={styles.errorText}>Please contact support to change an existing External ID</Text>}
  </ScrollView>;
};

const styles = StyleSheet.create({
  editFloorContainer: {
    paddingHorizontal: 30,
    alignSelf: 'stretch',
  },
  textInputTitle: {
    marginBottom: 4,
    textAlign: 'left',
    alignSelf: 'flex-start',
  },
  textInput: {
    fontSize: 16,
    lineHeight: Platform.OS !== 'ios' ? undefined : 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    height: 44,
    borderRadius: 5,
    paddingVertical: 10,
    paddingHorizontal: 8,
    backgroundColor: Colors.light.hereworksWhite200,
    borderWidth: 1,
    outlineWidth: 0,
    borderColor: Colors.light.hereworksBlue100,
  },
  textInputError: {
    borderColor: Colors.light.hereworksRed500,
  },
  textInputRow: {
    width: '100%',
    marginBottom: 16,
  },
  textField: {
    flex: 1,
    marginEnd: 8,
    alignSelf: 'flex-start',
  },
  errorText: {
    fontSize: 12,
    lineHeight: 17.5,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksRed500,
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    marginTop: 4,
    marginBottom: 32,
    alignSelf: 'center',
    textAlign: 'center',
  },
  priorityIndicator: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 4,
    backgroundColor: '#FFF',
    position: 'absolute',
    end: 20,
    top: 44,
  },
  priorityDropdown: {
    backgroundColor: '#FFF',
    position: 'absolute',
    borderWidth: 1.5,
    borderRadius: 10,
    borderColor: Colors.light.hereworksBlue100,
    overflow: 'hidden',
    end: 19,
    top: -20,
    zIndex: 2,
  },
  priorityText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    marginStart: 2,
  },
  dropdownIcon: {
    marginStart: 2,
  },
  optionText: {
    paddingHorizontal: 12,
    paddingVertical: 9,
    color: Colors.light.hereworksBlack600,
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
  },
  optionTextSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    color: '#FFF',
  },
});

export default FloorDetailsForm;
